// PWA notification scheduling for NegoIA.
//
// Uses the Notification Triggers API (`showTrigger` + `TimestampTrigger`) to
// schedule local notifications that fire even when the app is closed, on
// browsers that support it. On every app load (when permission is granted) we
// re-arm the next occurrence of each reminder — so as long as the owner opens
// the app periodically, the weekly/quincena/fin-de-mes reminders stay queued.
//
// No external push server / VAPID keys are required.

const DECLINE_KEY = "negoia:notif_declined_until";
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

export function notificationsSupported() {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator
  );
}

// Show the in-app pre-prompt only when the browser hasn't decided yet AND the
// user hasn't declined within the last 30 days.
export function shouldAskPermission() {
  if (!notificationsSupported()) return false;
  if (Notification.permission !== "default") return false;
  try {
    const until = Number(localStorage.getItem(DECLINE_KEY) || 0);
    if (until && Date.now() < until) return false;
  } catch {
    /* ignore */
  }
  return true;
}

export function declineNotifications() {
  try {
    localStorage.setItem(DECLINE_KEY, String(Date.now() + THIRTY_DAYS_MS));
  } catch {
    /* ignore */
  }
}

// Called when the user accepts the pre-prompt. Requests the native permission,
// registers the SW and schedules the reminders. Declines suppress for 30 days.
export async function enableNotifications(perfil) {
  if (!notificationsSupported()) return false;
  let permission = "denied";
  try {
    permission = await Notification.requestPermission();
  } catch {
    permission = "denied";
  }
  if (permission !== "granted") {
    declineNotifications();
    return false;
  }
  await scheduleReminders(perfil);
  return true;
}

// Re-arm the schedule on each load if permission is already granted.
export async function ensureScheduled(perfil) {
  if (!notificationsSupported()) return;
  if (Notification.permission !== "granted") return;
  await scheduleReminders(perfil);
}

function at8am(date) {
  date.setHours(8, 0, 0, 0);
  return date;
}

// Next Monday (dow 1) at 08:00 local.
function nextMonday8am(now = new Date()) {
  const d = at8am(new Date(now));
  let add = (1 - d.getDay() + 7) % 7; // 1 = Monday
  if (add === 0 && now.getTime() >= d.getTime()) add = 7;
  d.setDate(d.getDate() + add);
  return d;
}

// Next day-14 at 08:00 local.
function nextDay14At8am(now = new Date()) {
  let d = at8am(new Date(now.getFullYear(), now.getMonth(), 14));
  if (now.getTime() >= d.getTime()) {
    d = at8am(new Date(now.getFullYear(), now.getMonth() + 1, 14));
  }
  return d;
}

// Next last-day-of-month at 08:00 local (31st or whatever the month's last day is).
function nextLastDay8am(now = new Date()) {
  let d = at8am(new Date(now.getFullYear(), now.getMonth() + 1, 0));
  if (now.getTime() >= d.getTime()) {
    d = at8am(new Date(now.getFullYear(), now.getMonth() + 2, 0));
  }
  return d;
}

async function scheduleReminders(perfil) {
  let reg;
  try {
    reg = await navigator.serviceWorker.register("/sw.js");
    reg = await navigator.serviceWorker.ready;
  } catch {
    return;
  }

  // Notification Triggers API is required to fire while the app is closed.
  const supportsTrigger =
    typeof window !== "undefined" && "TimestampTrigger" in window;
  if (!supportsTrigger) return;

  const negocio = (perfil && perfil.nombreNegocio) || "tu negocio";
  const items = [
    {
      tag: "negoia-lunes",
      when: nextMonday8am(),
      body: `☀️ Nueva semana, nueva oportunidad para ${negocio}. ¿Qué promocionamos hoy?`,
    },
    {
      tag: "negoia-quincena",
      when: nextDay14At8am(),
      body: "💰 Mañana es quincena — el mejor momento del mes para promocionar tu negocio. ¿Empezamos?",
    },
    {
      tag: "negoia-finmes",
      when: nextLastDay8am(),
      body: "📅 Fin de mes — buen momento para revisar cómo te fue y planear el siguiente. Entra a NegoIA.",
    },
  ];

  for (const it of items) {
    try {
      await reg.showNotification("NegoIA", {
        body: it.body,
        tag: it.tag,
        renotify: true,
        // eslint-disable-next-line no-undef
        showTrigger: new TimestampTrigger(it.when.getTime()),
        data: { url: "/" },
      });
    } catch {
      /* scheduling failed for this item — skip */
    }
  }
}
